import * as React from "react";
import { InputGroup, InputGroupAddon, InputGroupInput } from "../ui/input-group";
import { Search, Loader, X } from "lucide-react";
import { Tooltip, TooltipContent, TooltipTrigger } from "../ui/tooltip";
import { Popover, PopoverContent, PopoverTrigger } from "../ui/popover";
import { useRequestGallery } from "./RequestGalleryContext";
import { cn } from "@/lib/utils";
import { Request } from "@/types/research.types";
import GalleryHeader from "./GalleryHeader";

const CreateRequestForm = React.lazy(() =>
  import("./CreateRequest").then((module) => ({ default: module.CreateRequestForm }))
);
const CreateRequestCategoryForm = React.lazy(() =>
  import("./CreateRequestCategory").then((module) => ({
    default: module.CreateRequestCategoryForm,
  }))
);
const EditRequestCategoryForm = React.lazy(() =>
  import("./EditRequestCategory").then((module) => ({ default: module.EditRequestCategoryForm }))
);
const EditRequestForm = React.lazy(() =>
  import("./EditRequest").then((module) => ({ default: module.EditRequestForm }))
);
const RequestCategoryList = React.lazy(() => import("./RequestCategoryList"));
const RequestList = React.lazy(() => import("./RequestList"));
import ExecuteAutomationDialog from "./ExecuteAutomationDialog";

const GalleryBody: React.FC<{
  mode: string;
  selectedCategoryId: string | null;
  editingCategory: { name: string } | null;
  editingRequest: Request | null;
  handleBack: () => void;
  handleRequestClick: (content: string, request?: Request) => void;
  type: "discuss" | "research";
}> = ({
  mode,
  selectedCategoryId,
  editingCategory,
  editingRequest,
  handleBack,
  handleRequestClick,
  type,
}) => (
  <React.Suspense
    fallback={
      <div className="flex flex-col items-center justify-center h-full gap-4">
        <Loader className="animate-spin" size={48} />
        <p>Loading...</p>
      </div>
    }
  >
    {mode === "list" ? (
      selectedCategoryId != null ? (
        <RequestList onRequestSelect={handleRequestClick} />
      ) : (
        <RequestCategoryList onRequestSelect={handleRequestClick} />
      )
    ) : mode === "create-category" ? (
      <CreateRequestCategoryForm />
    ) : mode === "create-request" ? (
      <CreateRequestForm />
    ) : mode === "edit-category" && editingCategory != null ? (
      <EditRequestCategoryForm />
    ) : mode === "edit-request" && editingRequest != null ? (
      <EditRequestForm
        request={editingRequest}
        onSuccess={handleBack}
        onCancel={handleBack}
        type={type}
      />
    ) : null}
  </React.Suspense>
);

const GallerySearchBar: React.FC<{
  searchQuery: string;
  setSearchQuery: (value: string) => void;
}> = ({ searchQuery, setSearchQuery }) => (
  <div className="p-2 border-b">
    <InputGroup>
      <InputGroupAddon>
        <Search className="size-3.5 text-neutral-500" />
      </InputGroupAddon>
      <InputGroupInput
        placeholder="Search categories and requests..."
        value={searchQuery}
        className="placeholder:text-neutral-500"
        onChange={(e) => setSearchQuery(e.target.value)}
      />
      {searchQuery !== "" && (
        <InputGroupAddon align="inline-end">
          <Tooltip>
            <TooltipTrigger asChild>
              <X
                className="size-3.5 text-neutral-500 cursor-pointer hover:text-neutral-300"
                onClick={() => setSearchQuery("")}
              />
            </TooltipTrigger>
            <TooltipContent>Clear search</TooltipContent>
          </Tooltip>
        </InputGroupAddon>
      )}
    </InputGroup>
  </div>
);

function useExecuteAutomation(
  onSelect: ((content: string, request?: Request) => void) | undefined,
  closeGallery: () => void
) {
  const [isOpen, setIsOpen] = React.useState(false);
  const [request, setRequest] = React.useState<Request | null>(null);

  const handleRequestClick = (content: string, req?: Request) => {
    if (req?.chain_with && req.chain_with.length > 0) {
      setRequest(req);
      setIsOpen(true);
    } else if (onSelect) {
      onSelect(content, req);
      closeGallery();
    }
  };

  const confirm = () => {
    if (request && onSelect) {
      onSelect(request.content, request);
      setRequest(null);
      setIsOpen(false);
      closeGallery();
    }
  };

  return { isOpen, setIsOpen, request, handleRequestClick, confirm };
}

const RequestGalleryContent: React.FC = () => {
  const ctx = useRequestGallery();
  const automation = useExecuteAutomation(ctx.onSelect, ctx.closeGallery);

  return (
    <div className="rounded-md flex flex-col h-full">
      <GalleryHeader
        mode={ctx.mode}
        handleBack={ctx.handleBack}
        selectedCategoryId={ctx.selectedCategoryId}
        currentParentId={ctx.currentParentId}
        handleBackToCategories={ctx.handleBackToCategories}
        handleBackToParentCategory={ctx.handleBackToParentCategory}
        selectedCategory={ctx.selectedCategory}
        currentCategory={ctx.currentCategory}
        setMode={ctx.setMode}
        onClose={ctx.closeGallery}
      />
      {ctx.mode === "list" && (
        <GallerySearchBar searchQuery={ctx.searchQuery} setSearchQuery={ctx.setSearchQuery} />
      )}
      <div
        className={cn(
          ctx.mode === "list"
            ? "flex-1 overflow-auto flex flex-col p-2 gap-2"
            : "flex-1 flex flex-col overflow-hidden p-0"
        )}
      >
        <GalleryBody
          mode={ctx.mode}
          selectedCategoryId={ctx.selectedCategoryId}
          editingCategory={ctx.editingCategory}
          editingRequest={ctx.editingRequest}
          handleBack={ctx.handleBack}
          handleRequestClick={automation.handleRequestClick}
          type={ctx.type}
        />
      </div>
      <ExecuteAutomationDialog
        isOpen={automation.isOpen}
        onClose={() => automation.setIsOpen(false)}
        onConfirm={automation.confirm}
        request={automation.request}
      />
    </div>
  );
};

const RequestGallery = React.forwardRef<
  HTMLButtonElement,
  {
    children: React.ReactNode;
    id: string;
    type?: "discuss" | "research";
    tooltip?: string;
  }
>(({ children, id, type, tooltip }, ref) => {
  const { activeGalleryId, closeGallery, setType } = useRequestGallery();
  const isOpen = activeGalleryId === id;

  React.useEffect(() => {
    if (isOpen && type) {
      setType(type);
    }
  }, [isOpen, type, setType]);

  const trigger = (
    <PopoverTrigger asChild ref={ref}>
      {children}
    </PopoverTrigger>
  );

  return (
    <Popover open={isOpen} onOpenChange={(open) => !open && closeGallery()}>
      {tooltip != null ? (
        <Tooltip>
          <TooltipTrigger asChild>{trigger}</TooltipTrigger>
          <TooltipContent>
            <p>{tooltip}</p>
          </TooltipContent>
        </Tooltip>
      ) : (
        trigger
      )}
      <PopoverContent className="p-0 w-[calc(100vw-2rem)] h-[500px]" side="top" align="start">
        <RequestGalleryContent />
      </PopoverContent>
    </Popover>
  );
});

RequestGallery.displayName = "RequestGallery";

export default RequestGallery;
